import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../Services/api";
import { useToast } from "../Hooks/toast";

type RefreshOptions = {
  notify?: boolean;
  force?: boolean;
};

interface SupportContextData {
  unread: number;
  setUnread: (value: number) => void;
  refreshUnread: (opts?: RefreshOptions) => Promise<number | null>;
}

const SupportContext = createContext<SupportContextData>({} as SupportContextData);

const MIN_REFRESH_INTERVAL_MS = 60_000;

export const SupportProvider: React.FC = ({ children }) => {
  const [unread, setUnread] = useState<number>(0);
  const { addToast } = useToast();

  const unreadRef = useRef(0);
  const lastFetchAtRef = useRef(0);
  const inFlightRef = useRef<Promise<number | null> | null>(null);
  const addToastRef = useRef(addToast);

  unreadRef.current = unread;
  addToastRef.current = addToast;

  const refreshUnread = useCallback(async (opts?: RefreshOptions) => {
    const now = Date.now();
    const force = opts?.force ?? false;

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    if (!force && now - lastFetchAtRef.current < MIN_REFRESH_INTERVAL_MS) {
      return unreadRef.current;
    }

    const request = (async () => {
      try {
        const { data } = await api.get("/support/unreads");
        const count = typeof data === "number" ? data : parseInt(data, 10);
        const safeCount = isNaN(count) ? 0 : Math.max(0, count);

        lastFetchAtRef.current = Date.now();
        setUnread(safeCount);
        unreadRef.current = safeCount;

        if ((opts?.notify ?? true) && safeCount > 0) {
          addToastRef.current({
            type: "info",
            title: "Suporte",
            description: `Você tem ${safeCount} mensagens novas do suporte`,
            linkTo: "/suporte",
            linkLabel: "Abrir suporte",
          });
        }

        return safeCount;
      } catch {
        return null;
      } finally {
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = request;
    return request;
  }, []);

  const value = useMemo(
    () => ({ unread, setUnread, refreshUnread }),
    [unread, refreshUnread]
  );

  return (
    <SupportContext.Provider value={value}>{children}</SupportContext.Provider>
  );
};

export const useSupport = () => {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error("useSupport must be used within SupportProvider");
  return ctx;
};
